import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { AnchorWithTitle } from '@/components/links/anchor';
import { LinkWithTitle } from '@/components/links';

export const DOWNTOWN_TAMPA_TITLE = 'Downtown Tampa';

export const Head = () => {
  return (
    <ComponentsHead
      title="Explore Downtown Tampa by Boat | Charter with TBBA"
      description="See Downtown Tampa like never before. Charter a private boat to cruise past Amalie Arena, Curtis Hixon Park, and the vibrant city skyline."
      keywords="Downtown Tampa, City Cruise, Landmarks, Tampa River"
    />
  );
};
const Page: FC = () => {
  return (
    <AddOnsTemplate
      title="Downtown Tampa Boat Charters – Cruise Past the City’s Best Views"
      subtitle=""
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DOWNTOWN_TAMPA_TITLE} header`}
              src="../../images/areas/downtown-tampa/0.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DOWNTOWN_TAMPA_TITLE} slide 1`}
              src="../../images/areas/downtown-tampa/1.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DOWNTOWN_TAMPA_TITLE} slide 2`}
              src="../../images/areas/downtown-tampa/2.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
      <p>
        There’s no better way to explore Tampa than from the
        water. Tampa Bay Boating Adventures offers luxury
        boat charters that cruise through the city’s most
        iconic waterways. From vibrant cityscapes to natural
        estuaries, Tampa offers a little bit of everything.
        Whether you're planning a full-day private party
        boat experience or a laid-back sunset cruise, our
        vessels offer front-row access to some of Tampa’s
        best views.
      </p>
      <p>
        We depart from Davis Island Boat Ramp and navigate
        through Tampa’s scenic bayfront, often passing by
        notable areas like Harbour Island, the{' '}
        <AnchorWithTitle href="https://www.ut.edu/">
          University of Tampa
        </AnchorWithTitle>
        , and the Downtown skyline. It’s the perfect way to
        experience the beauty of Tampa from a fresh, breezy
        perspective.
      </p>
      <p>
        Along the way, we can drop anchor for a swim,
        island-hop, or just cruise in style. Our experienced
        captains know the best spots to sightsee, relax, or
        celebrate.
      </p>
      <h3>Available Charters in Tampa:</h3>
      <ul>
        <li>
          <LinkWithTitle to="/charters/8-hour-charter/">
            8-Hour Private Party Boat
          </LinkWithTitle>
          : Spend the entire day exploring the bay,
          swimming, lounging, and celebrating with your
          crew. Ideal for birthdays, bachelor/bachelorette
          parties, or full-day adventures.
        </li>
        <li>
          <LinkWithTitle to="/charters/6-hour-charter/">
            6-Hour Private Party Boat
          </LinkWithTitle>
          : A perfect middle-ground charter that gives you
          time to cruise, relax, visit sandbars, and soak in
          the sights without rushing.
        </li>
        <li>
          <LinkWithTitle to="/charters/4-hour-charter/">
            4-Hour Private Party Boat
          </LinkWithTitle>
          : Great for a half-day escape. Ideal for groups
          who want to get out on the water for swimming,
          sightseeing, or a mini celebration.
        </li>
        <li>
          <LinkWithTitle to="/charters/3-hour-charter/">
            3-Hour Sunset Charter
          </LinkWithTitle>
          : Cruise into golden hour with a peaceful,
          picturesque evening charter. Watch the sun set
          behind Tampa’s skyline or out across the bay.
        </li>
        <li>
          <LinkWithTitle to="/charters/wave-rave-charter/">
            3-Hour Wave Rave Charter
          </LinkWithTitle>
          : A high-energy experience for those who want
          music, movement, and plenty of fun packed into a
          short trip.
        </li>
        <li>
          <LinkWithTitle to="/charters/snorkeling/">
            Snorkeling Charters
          </LinkWithTitle>
          : Dive in and explore Tampa Bay’s underwater
          world. These charters focus on Egmont Key in St
          Petersburg for snorkeling and marine life viewing.
        </li>
        <li>
          <LinkWithTitle to="/charters/large-group-charters/">
            Large Group Boat Charter
          </LinkWithTitle>
          : Ideal for corporate events, team outings, or
          just a bigger bash—this charter offers panoramic
          city views and all the space you need to celebrate
          in style.
        </li>
      </ul>
    </AddOnsTemplate>
  );
};

export default Page;
